import { defineStore } from 'pinia'

import DashboardResource from '@/api/resources/dashboard'
import GroupResource from '@/api/resources/group'
import SensorResource from '@/api/resources/sensor'
import type { IGroup } from '@/interfaces/entities/group'
import type { ISensor } from '@/interfaces/entities/sensor'
import type { IShift } from '@/interfaces/entities/shifts'
import type {
  DashboardViewMode,
  IDashboardSearch,
  IDashboardStore,
  IPerformDashboardSearchPayload,
} from '@/interfaces/stores/dashboard'
import { i18n } from '@/plugins/i18n'
import { useCompanyStore } from '@/store/company'
import { useErrorStore } from '@/store/error'
import { reportErrorMessage } from '@/services/report-error'
import { buildDashboardReportPayload } from '@/utils/dashboard/build-dashboard-report-payload'
import { AnalyticsEvents, logFirebaseAnalytics } from '@/utils/firebase'
import {
  buildDashboardSearchCacheKey,
  dedupeDashboardSearchColors,
  getDashboardPeriodKey,
  normalizeDashboardPeriod,
  pickNextDashboardSearchColor,
} from '@/utils/dashboard-workspace'
import { formatPeriodDate, getAnalysisPeriodRange } from '@/utils/date/date-time'

function emptyGroup(): IGroup {
  return {
    id: 0,
    name: '',
    observation: null,
    is_line_setup: false,
    active_power_outage_alarm: false,
    sensors: [],
  }
}

function emptySensor(): ISensor {
  return {} as ISensor
}

export const useDashboardStore = defineStore({
  id: 'dashboard',
  persist: {
    paths: ['searches', 'viewMode', 'sideBySidePreferred', 'isPanelExpanded', 'lastSearchType'],
  },

  state: (): IDashboardStore => ({
    searches: [],
    viewMode: 'list',
    sideBySidePreferred: false,
    isPanelExpanded: true,
    sensors: [] as ISensor[],
    sensor: emptySensor(),
    group: emptyGroup(),
    period: [] as Date[],
    shift: null,
    lastSearchType: 'sensor',
    scrollToSearchId: null,
    isGeneratingReport: false,
  }),

  getters: {
    getPeriodFilter: (state) => normalizeDashboardPeriod(state.period),
    getSensorFilter: (state) => state.sensor,
    listSensors: (state) => state.sensors,
    getShiftFilter: (state) => state.shift,
    getGroupFilter: (state) => state.group,
    getLastSearchType: (state) => state.lastSearchType,
    isGroupSelected: (state) => state.lastSearchType === 'group',
    isSensorSelected: (state) => state.lastSearchType === 'sensor',

    visibleSearches(state): IDashboardSearch[] {
      return state.searches.filter((s) => s.visible)
    },

    canGenerateReport(state): boolean {
      return state.searches.some((s) => s.visible)
    },

    unifiedChartPeriodKeys(state): string[] {
      const counts = new Map<string, number>()
      for (const s of state.searches) {
        if (!s.visible || s.type !== 'sensor') continue
        const k = getDashboardPeriodKey(s)
        counts.set(k, (counts.get(k) ?? 0) + 1)
      }
      return [...counts.entries()].filter(([, n]) => n >= 2).map(([k]) => k)
    },

    visibleSearchesCount(state): number {
      return state.searches.filter((s) => s.visible).length
    },

    canUseSideBySide(state): boolean {
      return state.searches.filter((s) => s.visible).length >= 2
    },
  },

  actions: {
    revivePersistedSearchPeriods() {
      const normalized = this.searches.map((s) => ({
        ...s,
        period: normalizeDashboardPeriod(s.period),
      }))
      this.searches = dedupeDashboardSearchColors(normalized)
    },

    setScrollToSearchId(id: string | null) {
      this.scrollToSearchId = id
    },

    clearScrollToSearchId() {
      this.scrollToSearchId = null
    },

    setViewMode(mode: DashboardViewMode) {
      if (mode === 'side-by-side' && !this.canUseSideBySide) {
        this.viewMode = 'list'
        return
      }
      if (mode === 'side-by-side') {
        this.sideBySidePreferred = true
      } else if (mode === 'list') {
        this.sideBySidePreferred = false
      }
      this.viewMode = mode
    },

    toggleViewMode() {
      if (this.viewMode === 'list') {
        this.setViewMode('side-by-side')
      } else {
        this.sideBySidePreferred = false
        this.viewMode = 'list'
      }
    },

    /** Mantém lista se <2 visíveis; restaura lado a lado se o utilizador já o tinha escolhido. */
    syncViewModeWithVisibleCount() {
      if (!this.canUseSideBySide) {
        if (this.viewMode === 'side-by-side') {
          this.viewMode = 'list'
        }
        return
      }
      if (this.sideBySidePreferred) {
        this.viewMode = 'side-by-side'
      }
    },

    togglePanelExpanded() {
      this.isPanelExpanded = !this.isPanelExpanded
    },

    setPanelExpanded(value: boolean) {
      this.isPanelExpanded = value
    },

    async fetchSensors() {
      try {
        this.sensors = await SensorResource.getAllSensors()
      } catch {
        // Erro ao buscar sensores
      }
    },

    setSensorFilter(sensor: ISensor) {
      this.sensor = sensor
    },

    setShiftFilter(shift: IShift | null) {
      this.shift = shift
    },

    setGroupFilter(group: IGroup) {
      this.group = group
    },

    setPeriodFilter(period: Date[]) {
      this.period = period
    },

    setLastSearchType(type: 'group' | 'sensor' | '') {
      this.lastSearchType = type
    },

    toggleSearchVisibility(searchId: string) {
      const item = this.searches.find((s) => s.id === searchId)
      if (!item) return
      item.visible = !item.visible
      this.syncViewModeWithVisibleCount()
    },

    setSearchVisible(searchId: string, visible: boolean) {
      const item = this.searches.find((s) => s.id === searchId)
      if (!item) return
      item.visible = visible
      this.syncViewModeWithVisibleCount()
    },

    removeSearch(searchId: string) {
      this.searches = this.searches.filter((s) => s.id !== searchId)
      this.syncViewModeWithVisibleCount()
    },

    clearAllSearches() {
      this.searches = []
      this.viewMode = 'list'
      this.sideBySidePreferred = false
      this.isPanelExpanded = true
      this.scrollToSearchId = null
      this.sensor = emptySensor()
      this.group = emptyGroup()
      this.period = []
      this.shift = null
    },

    /**
     * Empilha uma nova busca ou reativa duplicata (sem nova requisição).
     * @returns id da busca alvo (existente ou nova).
     */
    async performSearch(payload: IPerformDashboardSearchPayload): Promise<{ searchId: string; isDuplicate: boolean }> {
      const errorStore = useErrorStore()
      const companyStore = useCompanyStore()

      const period = payload.period
      if (!period?.length || period.length < 2) {
        errorStore.setErrors([i18n.global.t('dashboard.errors.invalid_period')])
        throw new Error('invalid_period')
      }

      const dateRangeQuery = formatPeriodDate(period)
      const shiftId = payload.shift != null && typeof payload.shift.id === 'number' ? payload.shift.id : null

      const mode = payload.mode
      const entityId = mode === 'sensor' ? payload.sensor!.id : payload.group!.id
      const cacheKey = buildDashboardSearchCacheKey(mode, entityId, dateRangeQuery, shiftId)

      const existing = this.searches.find((s) => s.cacheKey === cacheKey)
      if (existing) {
        existing.visible = true
        this.scrollToSearchId = existing.id
        this.syncViewModeWithVisibleCount()
        return { searchId: existing.id, isDuplicate: true }
      }

      const id = crypto.randomUUID()
      const color = pickNextDashboardSearchColor(this.searches.map((s) => s.color))
      const label = mode === 'sensor' ? payload.sensor!.name : payload.group!.name

      const base: IDashboardSearch = {
        id,
        cacheKey,
        type: mode,
        sensor: mode === 'sensor' ? payload.sensor! : null,
        group: mode === 'group' ? payload.group! : null,
        label,
        period: [...period],
        dateRangeQuery,
        shiftId,
        totalizer: {} as IDashboardSearch['totalizer'],
        mixingCycleData: null,
        mixingCycleLoading: false,
        visible: true,
        color,
      }

      try {
        if (mode === 'sensor') {
          const totalizer = await SensorResource.getSensorTotalizer({
            sensorId: payload.sensor!.id,
            dateRange: dateRangeQuery,
            shiftId,
          })
          logFirebaseAnalytics(AnalyticsEvents.DASHBOARD.SENSOR_TOTALIZER)
          base.totalizer = totalizer

          if (companyStore.hasMixingCyclesEnabled) {
            const { startDate, endDate } = getAnalysisPeriodRange(period)
            base.mixingCycleLoading = true
            try {
              base.mixingCycleData = await SensorResource.getMixingCycles({
                sensorId: payload.sensor!.id,
                startDate,
                endDate,
              })
            } catch {
              base.mixingCycleData = null
            } finally {
              base.mixingCycleLoading = false
            }
          }
        } else {
          const totalizer = await GroupResource.getGroupTotalizer({
            groupId: payload.group!.id,
            dateRange: dateRangeQuery,
            shiftId,
          })
          logFirebaseAnalytics(AnalyticsEvents.DASHBOARD.GROUP_TOTALIZER)
          base.totalizer = totalizer
        }

        this.searches.push(base)
        this.scrollToSearchId = id
        this.syncViewModeWithVisibleCount()
        return { searchId: id, isDuplicate: false }
      } catch {
        errorStore.setErrors([i18n.global.t('dashboard.errors.load_failed')])
        throw new Error('fetch_failed')
      }
    },

    async generateReport() {
      if (!this.canGenerateReport || this.isGeneratingReport) return

      this.isGeneratingReport = true
      const companyStore = useCompanyStore()

      try {
        const payload = buildDashboardReportPayload({
          visibleSearches: this.visibleSearches,
          companyName: companyStore.getCurrentCompany?.name ?? 'Empresa',
          shifts: companyStore.shifts,
        })

        const response = await DashboardResource.generateReport(payload)
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const disposition = (response.headers?.['content-disposition'] as string | undefined) ?? ''
        const match = disposition.match(/filename="?([^"]+)"?/)
        const filename = match?.[1] || 'dashboard-report.pdf'

        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } catch (error) {
        useErrorStore().setErrors([reportErrorMessage(error, i18n.global.t('dashboard.pdf_modal.error'))])
      } finally {
        this.isGeneratingReport = false
      }
    },
  },
})
